
/**
 * IKIDS POMPEI
 * ORDINAMENTO VISIVO TAGLIE GLOBO
 *
 * Compatibile con:
 * - Pagina prodotto
 * - Card prodotto nelle collezioni
 * - Prodotti caricati dinamicamente
 *
 * Non modifica:
 * - Varianti Shopify
 * - ID varianti
 * - Prezzi
 * - Inventario
 * - Input originali
 * - Ordine varianti nell'admin
 *
 * Utilizza esclusivamente CSS order.
 */

(function () {
  'use strict';

  // ==========================================
  // 1. CONFIGURAZIONE GLOBO
  // ==========================================

  const GROUP_SELECTOR = '.swatch--gl';

  const SIZE_LABELS = new Set([
    'taglia',
    'taglie',
    'size',
    'sizes'
  ]);

  // ==========================================
  // 2. MAPPATURA TAGLIE ALFABETICHE
  // ==========================================

  const ALPHA = {
    xxxs: 0,
    xxs: 1,
    xs: 2,
    'xs-s': 2.5,
    s: 3,
    m: 4,
    l: 5,
    xl: 6,
    xxl: 7,
    '2xl': 7,
    xxxl: 8,
    '3xl': 8,
    '4xl': 9,
    '5xl': 10,
    jr: 11
  };

  // ==========================================
  // 3. MAPPATURA TAGLIE ROMANE
  // ==========================================

  const ROMAN = {
    i: 1,
    ii: 2,
    iii: 3,
    lll: 3,
    iv: 4
  };

  // ==========================================
  // 4. MAPPATURA TAGLIE PARTICOLARI
  // ==========================================

  const SPECIAL_AGES = {
    'xs (4-5 anni)': [4, 5],
    '4 anni (xs)': [4, 4],
    '5 anni (xs)': [5, 5],
    's (6-7 anni)': [6, 7],
    '6 anni (s)': [6, 6],
    'm (8-9 anni)': [8, 9],
    'l (10-11 anni)': [10, 11],
    'xl (12-13 anni)': [12, 13],
    'xxl (14-15 anni)': [14, 15],
    'jr (16 anni)': [16, 16]
  };

  // ==========================================
  // 5. NORMALIZZAZIONE
  // ==========================================

  function clean(value) {
    return String(value == null ? '' : value)
      .normalize('NFKC')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/,/g, '.');
  }

  // ==========================================
  // 6. INTERPRETAZIONE TAGLIE
  // ==========================================

  function key(raw) {
    const s = clean(raw);

    // TAGLIA UNICA
    if (
      s === 'taglia unica' ||
      s === 'unica' ||
      s === 'one size' ||
      s === 'tu'
    ) {
      return {
        kind: 'unique',
        start: 0,
        end: 0
      };
    }

    // TAGLIE SPECIALI
    if (
      Object.prototype.hasOwnProperty.call(
        SPECIAL_AGES,
        s
      )
    ) {
      const [a, b] = SPECIAL_AGES[s];

      return {
        kind: 'age',
        start: a * 12,
        end: b * 12
      };
    }

    let m;

    // INTERVALLI CON UNITÀ DIFFERENTI
    // Esempi: 24m-4a, 3m-18m
    m = s.match(
      /^(\d+(?:\.\d+)?)\s*([ma])\s*[-/]\s*(\d+(?:\.\d+)?)\s*([ma])$/
    );

    if (m) {
      return {
        kind: 'age',
        start: Number(m[1]) *
          (m[2] === 'a' ? 12 : 1),
        end: Number(m[3]) *
          (m[4] === 'a' ? 12 : 1)
      };
    }

    // MESI E ANNI
    // Esempi:
    // 3 mesi, 18 mesi, 2 anni,
    // 2anni, 4a, 12m, 3-6 mesi
    m = s.match(
      /^(\d+(?:\.\d+)?)(?:\s*[-/]\s*(\d+(?:\.\d+)?))?\s*(mesi|mese|months?|mos?|anni|anno|years?|yrs?|[mya])$/
    );

    if (m) {
      const isYears =
        /^(anni|anno|years?|yrs?|a|y)$/
          .test(m[3]);

      const multiplier = isYears ? 12 : 1;

      return {
        kind: 'age',
        start: Number(m[1]) * multiplier,
        end: Number(m[2] ?? m[1]) * multiplier
      };
    }

    // TAGLIE ALFABETICHE
    if (
      Object.prototype.hasOwnProperty.call(
        ALPHA,
        s
      )
    ) {
      return {
        kind: 'alpha',
        start: ALPHA[s],
        end: ALPHA[s]
      };
    }

    // TAGLIE ROMANE
    if (
      Object.prototype.hasOwnProperty.call(
        ROMAN,
        s
      )
    ) {
      return {
        kind: 'roman',
        start: ROMAN[s],
        end: ROMAN[s]
      };
    }

    // TAGLIE NUMERICHE
    // Esempi: 22, 22.5, 36, 4/6, 10/12/14
    if (
      /^\d+(?:\.\d+)?(?:\s*[-/]\s*\d+(?:\.\d+)?)*$/
        .test(s)
    ) {
      const numbers = s
        .split(/\s*[-/]\s*/)
        .map(Number);

      return {
        kind: 'numeric',
        start: numbers[0],
        end: numbers[numbers.length - 1]
      };
    }

    // VALORE NON RICONOSCIUTO
    return {
      kind: 'unknown',
      start: Infinity,
      end: Infinity
    };
  }

  // ==========================================
  // 7. PRIORITÀ ORDINAMENTO
  // ==========================================

  const PRIORITY = {
    age: 0,
    numeric: 1,
    alpha: 2,
    roman: 3,
    unique: 4,
    unknown: 5
  };

  function compare(a, b) {
    const x = a.parsed;
    const y = b.parsed;

    return (
      PRIORITY[x.kind] - PRIORITY[y.kind] ||
      x.start - y.start ||
      x.end - y.end ||
      a.index - b.index
    );
  }

  // ==========================================
  // 8. LETTURA TAGLIE GLOBO
  // ==========================================

  function getSizeValue(item) {

    // PAGINA PRODOTTO
    // Manteniamo il metodo che funzionava:
    // lettura diretta dell'attributo data-value.
    if (item.matches('li.select-option')) {
      const dataValue =
        item.getAttribute('data-value');

      if (dataValue && dataValue.trim()) {
        return dataValue.trim();
      }

      return (
        item.textContent || ''
      ).trim();
    }

    // PAGINA COLLEZIONE
    // Globo utilizza li.swatches-options.
    if (item.matches('li.swatches-options')) {

      // Il testo completo del pulsante
      // normalmente contiene "4 anni",
      // mentre l'input può contenere solo "4".
      const fullText = (
        item.textContent || ''
      ).trim();

      // Prima verifichiamo se il testo completo
      // è già una taglia riconosciuta.
      if (
        fullText &&
        key(fullText).kind !== 'unknown'
      ) {
        return fullText;
      }

      // Seconda possibilità: testo della label.
      const labels = item.querySelectorAll('label');

      for (const label of labels) {
        const value = (
          label.textContent || ''
        ).trim();

        if (
          value &&
          key(value).kind !== 'unknown'
        ) {
          return value;
        }
      }

      // Terza possibilità: attributi Globo.
      const attributes = [
        item.getAttribute('data-value'),
        item.getAttribute('title')
      ];

      for (const value of attributes) {
        if (
          value &&
          key(value).kind !== 'unknown'
        ) {
          return value;
        }
      }

      // Ultima possibilità: input radio.
      const input = item.querySelector('input');

      return (
        input?.value ||
        fullText ||
        ''
      ).trim();
    }

    return (
      item.getAttribute('data-value') ||
      item.textContent ||
      ''
    ).trim();
  }

  // ==========================================
  // 9. ORDINAMENTO VISIVO GLOBO
  // ==========================================

  function sortGroup(group) {
    const legend = group.querySelector(
      'legend[data-name]'
    );

    if (!legend) return;

    const optionName = clean(
      legend.getAttribute('data-name')
    );

    // Modifichiamo esclusivamente le taglie.
    if (!SIZE_LABELS.has(optionName)) return;

    const list = group.querySelector('ul.value');

    if (!list) return;

    // Compatibile con pagina prodotto
    // e card delle collezioni.
    const items = Array.from(list.children)
      .filter(el => el.matches(
        'li.select-option, li.swatches-options'
      ));

    if (items.length < 2) return;

    // CSS order funziona solo in Flexbox o Grid.
    const display = window.getComputedStyle(
      list
    ).display;

    if (
      !display.includes('flex') &&
      !display.includes('grid')
    ) {
      return;
    }

    // Leggiamo le taglie originali.
    const entries = items.map((item, index) => {
      const sizeValue = getSizeValue(item);

      return {
        item,
        index,
        sizeValue,
        parsed: key(sizeValue)
      };
    });

    // Evita di ordinare un gruppo se
    // nessuna taglia viene riconosciuta.
    if (
      entries.every(entry =>
        entry.parsed.kind === 'unknown'
      )
    ) {
      return;
    }

    // Calcola l'ordine corretto.
    const sorted = [...entries].sort(compare);

    // Assegna le nuove posizioni visive.
    // Non modifica né sposta gli elementi DOM.
    sorted.forEach(({ item }, newIndex) => {
      const nextOrder = String(newIndex);

      const currentOrder =
        item.style.getPropertyValue('order');

      const currentPriority =
        item.style.getPropertyPriority('order');

      if (
        currentOrder !== nextOrder ||
        currentPriority !== 'important'
      ) {
        item.style.setProperty(
          'order',
          nextOrder,
          'important'
        );
      }
    });
  }

  // ==========================================
  // 10. AGGIORNAMENTI DINAMICI
  // ==========================================

  let queued = false;

  function schedule() {
    if (queued) return;

    queued = true;

    requestAnimationFrame(() => {
      queued = false;

      document
        .querySelectorAll(GROUP_SELECTOR)
        .forEach(sortGroup);
    });
  }

  // ==========================================
  // 11. AVVIO INIZIALE
  // ==========================================

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      schedule,
      { once: true }
    );
  } else {
    schedule();
  }

  // ==========================================
  // 12. OSSERVAZIONE GLOBO
  // ==========================================

  // Rileva nuove card, prodotti caricati
  // dinamicamente e aggiornamenti delle taglie.
  // Non osserva gli attributi style:
  // questo evita cicli infiniti.
  const observer = new MutationObserver(schedule);

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  // Aggiornamento sezioni Shopify
  document.addEventListener(
    'shopify:section:load',
    schedule
  );

})();

/* I-KIDS - Ordinamento taglie personalizzate in collezione */
(function () {
  "use strict";

  const alpha = {
    "XXXS": 0,
    "XXS": 1,
    "XS": 2,
    "XS-S": 2.5,
    "S": 3,
    "M": 4,
    "L": 5,
    "XL": 6,
    "XXL": 7,
    "2XL": 7,
    "XXXL": 8,
    "3XL": 8,
    "4XL": 9,
    "5XL": 10
  };

  const roman = {
    "I": 1,
    "II": 2,
    "III": 3,
    "IV": 4
  };

  function sizeKey(value) {
    const s = String(value || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, " ")
      .replace(",", ".");

    const number = parseFloat(s);

    if (/^\d/.test(s) && /\b(MESI|MESE|MONTHS?|ANNI|ANNO|YEARS?)\b/.test(s)) {
      return [0, number * (/MESI|MESE|MONTH/.test(s) ? 1 : 12)];
    }

    if (/^\d+(?:[.,]\d+)?\s*[MA]$/.test(s)) {
      return [0, number * (s.endsWith("M") ? 1 : 12)];
    }

    if (/^\d/.test(s)) {
      return [1, number];
    }

    if (Object.prototype.hasOwnProperty.call(alpha, s)) {
      return [2, alpha[s]];
    }

    if (Object.prototype.hasOwnProperty.call(roman, s)) {
      return [3, roman[s]];
    }

    return [4, s];
  }

  function sortSizes() {
    document.querySelectorAll(".ik-size-list").forEach(list => {
      const items = Array.from(
        list.querySelectorAll(":scope > .ik-size-option")
      );

      items.sort((a, b) => {
        const x = sizeKey(a.dataset.value);
        const y = sizeKey(b.dataset.value);

        return x[0] - y[0] ||
          (typeof x[1] === "number"
            ? x[1] - y[1]
            : String(x[1]).localeCompare(String(y[1]), "it"));
      });

      items.forEach((item, index) => {
        item.style.setProperty("order", index, "important");
      });
    });
  }

  let scheduled = false;

  function scheduleSort() {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      sortSizes();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", sortSizes);
  } else {
    sortSizes();
  }

  new MutationObserver(scheduleSort).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
